import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useAccountAuth } from '../hooks/useAccountAuth';
import { useEntitlement } from '../hooks/useEntitlement';
import { accessMessage } from '../lib/accessMessages';
import { readInvitedAccessStatus } from '../lib/beta';
import { openPrivacyPolicy } from '../lib/privacyPolicy';
import { TIPS_STORAGE_KEY } from '../utils/tipsPreference';
import { OPEN_COUNT_KEY, DISCOVERY_SEEN_KEY } from '../hooks/useDiscoveryTips';
import { track, resetAnalytics } from '../utils/analytics';
import { clearScopes, resetPullCursor } from '../data/userDataStore';
import { useDataContext } from '../data/DataProvider';
import { canRedeemInviteCode } from '../config/distribution';
import { canRestorePurchases } from '../lib/purchaseGate';
import { YouCard, SubScreenHeader, softButtonSx, dangerButtonSx, accentButtonSx } from './YouPrimitives';
import SignUpSheet from './SignUpSheet';
import BetaCodeDialog from './BetaCodeDialog';
import DeleteAccountDialog from './DeleteAccountDialog';

const cardTitleSx = { fontSize: 17, fontWeight: 600, color: '#fff', mb: '12px' };
const buttonSx = softButtonSx;

// Section "Account" du hub You. Elle ne porte plus le formulaire : s'inscrire
// se fait dans SignUpSheet, qui sert aussi le moment ou l'inscription compte
// vraiment (juste apres un code d'invitation). Il reste ici tout ce qui releve
// du compte : qui est connecte, d'ou vient l'acces, les achats, les donnees.
const AccountSection = ({ onBack }) => {
  const { user, signedIn, busy, error, setError, signOut } = useAccountAuth();
  const {
    fullApp,
    patreonAccess,
    patreonVerifiedAt,
    unlinkPatreon,
    betaAccess,
    betaVerifiedAt,
    disconnectBeta,
    restorePurchases,
    realPayments,
  } = useEntitlement();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [patreonBusy, setPatreonBusy] = useState(false);
  const [betaDialogOpen, setBetaDialogOpen] = useState(false);
  const [betaSignUpOpen, setBetaSignUpOpen] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState(null);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  // IndexedDB handle (user_data store) for the reset
  const { db } = useDataContext();

  // Un compte connecte sans le moindre acces : sans un mot ici, la section
  // affirme que tout suit le compte alors que rien n'a suivi, et la personne
  // n'a aucun moyen de deviner qu'elle s'est connectee au mauvais compte.
  const noAccessMessage =
    signedIn && !fullApp && !patreonAccess && !betaAccess
      ? accessMessage({ email: user?.email, status: readInvitedAccessStatus(user?.id) })
      : null;

  // Restore : natif et paiements réels, connecté ou non. La règle et ses
  // raisons vivent dans lib/purchaseGate, partagées avec la feuille de verrou.
  const showRestore = canRestorePurchases({ realPayments });

  const handleDisconnectBeta = () => {
    disconnectBeta();
    track('beta_disconnected');
  };

  const handleUnlinkPatreon = async () => {
    setPatreonBusy(true);
    await unlinkPatreon();
    setPatreonBusy(false);
    track('patreon_unlinked');
  };

  const handleRestorePurchases = async () => {
    setRestoreBusy(true);
    setRestoreMessage(null);
    const res = await restorePurchases();
    setRestoreBusy(false);
    track('purchases_restore_attempted', { restored: !!res.restored });
    if (res.success && res.restored) {
      setRestoreMessage('Your purchase has been restored.');
    } else if (res.success) {
      setRestoreMessage('No previous purchase found for this account.');
    } else {
      setRestoreMessage('Restore failed. Please try again.');
    }
  };

  // Reset user data: wipe settings + purchases, keep collections (progression/preset/pattern)
  const handleClearLocalStorage = async () => {
    track('settings_cleared');
    resetAnalytics();

    // Drop settings and purchases from user_data, preserve user collections
    await clearScopes(db, ['setting', 'purchase']);

    // La row `purchase`/`patreon` appartient au serveur et est plus ancienne que
    // le curseur de pull : sans remise a zero du curseur, elle ne reviendrait
    // dans aucun delta et l'utilisateur perdrait son acces Patreon.
    await resetPullCursor(db);

    // Local-only onboarding flags live in localStorage (outside user_data)
    localStorage.removeItem('onboardingCompleted');
    localStorage.removeItem('firstTimePopupSeen');
    localStorage.removeItem('hasOpenedBefore');
    localStorage.removeItem(OPEN_COUNT_KEY);
    localStorage.removeItem(DISCOVERY_SEEN_KEY);
    localStorage.removeItem(TIPS_STORAGE_KEY);

    setClearDialogOpen(false);
    window.location.reload();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <SubScreenHeader title="Account" onBack={onBack} />

      <YouCard>
        <Typography sx={cardTitleSx}>Account</Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {signedIn ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Signed in as <strong>{user.email}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: noAccessMessage ? 1 : 2 }}>
              Your settings and purchases sync to this account on all your devices.
            </Typography>
            {noAccessMessage && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {noAccessMessage}
              </Typography>
            )}
            <Button
              variant="contained"
              disableElevation
              onClick={signOut}
              disabled={busy}
              sx={dangerButtonSx}
            >
              Sign out
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You're playing as a guest. Everything you do lives on this device only. Create an
              account to keep your settings and everything you unlocked, everywhere.
            </Typography>
            <Button
              variant="contained"
              disableElevation
              onClick={() => setSheetOpen(true)}
              sx={accentButtonSx}
            >
              Create an account or sign in
            </Button>
          </Box>
        )}
      </YouCard>

      {/* Section distincte des achats : cet acces vient d'un abonnement chez un
          tiers, il est revocable, et il ne se "restaure" pas. */}
      {patreonAccess && (
        <YouCard>
          <Typography sx={cardTitleSx}>Patreon</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connected. Your membership unlocks the full app.
            {patreonVerifiedAt && ` Last checked ${patreonVerifiedAt.toLocaleDateString()}.`}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={handleUnlinkPatreon}
            disabled={patreonBusy}
            sx={buttonSx}
          >
            Disconnect Patreon
          </Button>
        </YouCard>
      )}

      {/* Saisie du code d'invitation, ou etat "connecte" avec deconnexion
          locale. L'acces est revocable (kill switch Admin) et re-valide au boot.
          La saisie disparait des builds natifs publics, mais un beneficiaire
          existant garde la carte : il doit pouvoir constater son acces et le
          retirer. Le vocabulaire beta n'apparait alors plus. */}
      {(betaAccess || canRedeemInviteCode()) && (
        <YouCard>
          <Typography sx={cardTitleSx}>
            {canRedeemInviteCode() ? 'Beta access' : 'Access'}
          </Typography>
          {betaAccess ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {canRedeemInviteCode()
                  ? 'Active. Your invitation unlocks the full app.'
                  : 'Active. The full app is unlocked on this account.'}
                {betaVerifiedAt && ` Last checked ${betaVerifiedAt.toLocaleDateString()}.`}
              </Typography>
              {/* Retire des builds publics le 01/09/2026. Un acces offert n'est
                  pas au beneficiaire de le defaire : sur natif public il n'y a
                  plus de champ de code (3.1.1), donc le retirer d'un tap etait
                  sans retour possible, et seul Christophe pouvait le reposer.
                  Le retrait vit maintenant dans le CMS. Le bouton reste dans les
                  builds internes, ou il sert a rejouer un parcours d'invitation
                  sans passer par l'Admin. */}
              {canRedeemInviteCode() && (
                <Button variant="contained" disableElevation onClick={handleDisconnectBeta} sx={buttonSx}>
                  Remove invitation code
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Got an invite? Enter your code to unlock the full app.
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={() => setBetaDialogOpen(true)}
                sx={buttonSx}
              >
                I have an invitation code
              </Button>
            </>
          )}
        </YouCard>
      )}

      {showRestore && (
        <YouCard>
          <Typography sx={cardTitleSx}>Purchases</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Already bought the app on another device or after a reinstall? Restore your purchase here.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={handleRestorePurchases}
            disabled={restoreBusy}
            sx={buttonSx}
          >
            Restore purchases
          </Button>
          {restoreMessage && (
            <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
              {restoreMessage}
            </Typography>
          )}
        </YouCard>
      )}

      {/* Deux exigences de review, la ou on les cherche : la politique de
          confidentialite atteignable depuis l'app, et la suppression du compte
          (guideline 5.1.1(v)). Le bouton reste visible en session invitee :
          elle aussi a un compte cote Supabase et des donnees cote serveur. */}
      <YouCard>
        <Typography sx={cardTitleSx}>Privacy and account data</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Read how upScale handles your data.
        </Typography>
        <Button variant="contained" disableElevation onClick={openPrivacyPolicy} sx={buttonSx}>
          Privacy policy
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
          Deleting your account permanently removes it, along with your settings, saved patterns
          and presets, on every device.
          {realPayments && ' Your App Store purchase is not cancelled: it stays on your Apple ID and can be restored later.'}
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={() => setDeleteAccountOpen(true)}
          sx={dangerButtonSx}
        >
          Delete my account
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
          Reset everything this device remembers: onboarding, tips, audio and tuning settings. Your
          saved presets and patterns stay.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={() => setClearDialogOpen(true)}
          sx={dangerButtonSx}
        >
          Reset local data
        </Button>
      </YouCard>

      <SignUpSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />

      {/* Meme enchainement que depuis la feuille de deverrouillage : un code
          valide debloque cet appareil, l'inscription qui suit l'emmene ailleurs.
          Non monte quand la saisie de code est fermee. */}
      {canRedeemInviteCode() && (
        <>
          <BetaCodeDialog
            open={betaDialogOpen}
            onClose={() => setBetaDialogOpen(false)}
            onSuccess={() => setBetaSignUpOpen(true)}
          />
          <SignUpSheet
            open={betaSignUpOpen}
            onClose={() => setBetaSignUpOpen(false)}
            variant="beta"
          />
        </>
      )}

      <DeleteAccountDialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        showPurchaseNote={realPayments}
        showPatreonNote={patreonAccess}
      />

      {/* Hidden Clear LocalStorage Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
      >
        <DialogTitle>Réinitialiser l'application ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action va supprimer toutes les données utilisateur (onboarding, tips, achats, paramètres audio et tuning).
            <br /><br />
            <strong>Vos presets sauvegardés seront conservés.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleClearLocalStorage} color="error" variant="contained" disableElevation>
            Réinitialiser
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSection;
